import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react'
import { authedFetch } from '../api'
import { Link } from 'react-router-dom'
import { useAppDispatch } from '../hooks'
import { fetchFailures } from '../slices/failuresSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import ProcessingSection from '../components/ProcessingSection'
import './ProcessingPage.css'

interface UncleanedFailure {
  id: string
  title: string
  short_id: string
  clean_status: string
  clean_error: string
  created_at: string
}

interface UnrefinedFailure {
  id: string
  title: string
  short_id: string
  refined_status: string
  refined_error: string
  clean_status: string
  created_at: string
}

interface AttemptEvent {
  id: string
  failure_id: string
  container_name: string
  attempt_number: number
  status: string
  error: string
  created_at: string
  ended_at: string | null
  failure_title: string
  failure_short_id: string
}

interface RunContainer {
  id: string
  container_name: string
  status: string
  run_id: string
  agent_name: string
  created_at: string
  run_name: string
  is_failure_run: boolean
  source_failure_id: string | null
  failure_title: string | null
  failure_short_id: string | null
}

interface PipelineFailure {
  id: string
  title: string
  short_id: string
  run_status: string
  run_error: string
  summarize_status: string
  summarize_error: string
}

interface PendingFailure {
  id: string
  title: string
  short_id: string
  run_status?: string
  run_error?: string
  summarize_status?: string
  summarize_error?: string
}

interface UnrootcausedFailure {
  id: string
  title: string
  short_id: string
  root_cause_status: string
  root_cause_error: string
  created_at: string
}

interface ProcessingData {
  uncleaned: UncleanedFailure[]
  unrefined: UnrefinedFailure[]
  unrootcaused: UnrootcausedFailure[]
  cleanEvents: AttemptEvent[]
  refineEvents: AttemptEvent[]
  rootCauseEvents: AttemptEvent[]
  assessmentEvents: unknown[]
  assessingCount: number
  maxAssessments: number
  runContainers: RunContainer[]
  pipelineFailures: PipelineFailure[]
  summarizingCount: number
  maxSummarizations: number
  pendingRunFailures: PendingFailure[]
  pendingSummarizeFailures: PendingFailure[]
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function ProcessingPage() {
  const dispatch = useAppDispatch()
  const [data, setData] = useState<ProcessingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [autoCleanEnabled, setAutoCleanEnabled] = useState(false)
  const [autoRefineEnabled, setAutoRefineEnabled] = useState(false)
  const [autoRootCauseEnabled, setAutoRootCauseEnabled] = useState(false)
  const [autoRunEnabled, setAutoRunEnabled] = useState(false)
  const [autoSummarizeEnabled, setAutoSummarizeEnabled] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    cleaning: false,
    refining: false,
    rootCause: false,
    run: false,
    summarize: false,
  })
  const pageStickyHeaderRef = useRef<HTMLElement>(null)
  const [sectionHeaderStickyTop, setSectionHeaderStickyTop] = useState(0)

  useLayoutEffect(() => {
    const el = pageStickyHeaderRef.current
    if (!el) return
    const update = () => setSectionHeaderStickyTop(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch('/api/processing-events')
      if (res.ok) {
        const json = await res.json() as ProcessingData
        setData(json)
      }
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
    dispatch(fetchFailures({ tag: null, search: null }))
    const interval = setInterval(() => {
      fetchData()
      dispatch(fetchFailures({ tag: null, search: null }))
    }, 15000)
    return () => clearInterval(interval)
  }, [fetchData, dispatch])

  useEffect(() => {
    fetch('/api/settings')
      .then((res) => res.ok ? res.json() as Promise<Record<string, string>> : ({} as Record<string, string>))
      .then((settings) => {
        setAutoCleanEnabled(settings.auto_clean_enabled === 'true')
        setAutoRefineEnabled(settings.auto_refine_enabled === 'true')
        setAutoRootCauseEnabled(settings.auto_root_cause_enabled === 'true')
        setAutoRunEnabled(settings.auto_run_enabled === 'true')
        setAutoSummarizeEnabled(settings.auto_summarize_enabled === 'true')
      })
      .catch(() => { /* ignore */ })
  }, [])

  const triggerSpawnWorkers = useCallback(async () => {
    await authedFetch('/api/spawn-workers-background', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
    dispatch(fetchFailures({ tag: null, search: null }))
    fetchData()
  }, [dispatch, fetchData])

  const handleToggleAutoClean = useCallback(async () => {
    const newValue = !autoCleanEnabled
    setAutoCleanEnabled(newValue)
    await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_clean_enabled: String(newValue) }),
    })
    if (newValue) await triggerSpawnWorkers()
  }, [autoCleanEnabled, triggerSpawnWorkers])

  const handleToggleAutoRefine = useCallback(async () => {
    const newValue = !autoRefineEnabled
    setAutoRefineEnabled(newValue)
    await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_refine_enabled: String(newValue) }),
    })
    if (newValue) await triggerSpawnWorkers()
  }, [autoRefineEnabled, triggerSpawnWorkers])

  const handleToggleAutoRootCause = useCallback(async () => {
    const newValue = !autoRootCauseEnabled
    setAutoRootCauseEnabled(newValue)
    await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_root_cause_enabled: String(newValue) }),
    })
    if (newValue) await triggerSpawnWorkers()
  }, [autoRootCauseEnabled, triggerSpawnWorkers])

  const handleToggleAutoRun = useCallback(async () => {
    const newValue = !autoRunEnabled
    setAutoRunEnabled(newValue)
    await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_run_enabled: String(newValue) }),
    })
    if (newValue) await triggerSpawnWorkers()
  }, [autoRunEnabled, triggerSpawnWorkers])

  const handleToggleAutoSummarize = useCallback(async () => {
    const newValue = !autoSummarizeEnabled
    setAutoSummarizeEnabled(newValue)
    await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_summarize_enabled: String(newValue) }),
    })
    if (newValue) await triggerSpawnWorkers()
  }, [autoSummarizeEnabled, triggerSpawnWorkers])

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const handleResetFailures = useCallback(async (stage: string) => {
    try {
      const res = await authedFetch('/api/reset-failures', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage }),
      })
      if (res.ok) {
        fetchData()
        dispatch(fetchFailures({ tag: null, search: null }))
      }
    } catch {
      // ignore
    }
  }, [fetchData, dispatch])

  const [syncingRunAgents, setSyncingRunAgents] = useState(false)
  const handleSyncRunAgents = useCallback(async () => {
    if (!confirm('Sync all failure runs to match currently enabled agents? This will add tasks for new agents, remove unstarted tasks for disabled agents, and reopen completed runs that need new tasks.')) return
    setSyncingRunAgents(true)
    try {
      const res = await authedFetch('/api/sync-run-agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        const result = await res.json()
        alert(`Synced ${result.runs_updated}/${result.total_runs} runs: ${result.tasks_added} tasks added, ${result.tasks_removed} removed, ${result.runs_reopened} runs reopened`)
        fetchData()
        dispatch(fetchFailures({ tag: null, search: null }))
      }
    } catch {
      // ignore
    } finally {
      setSyncingRunAgents(false)
    }
  }, [fetchData, dispatch])

  const [rerunningRootCauseJudge, setRerunningRootCauseJudge] = useState(false)
  const handleRerunRootCauseJudge = useCallback(async () => {
    if (!confirm('Rerun mechanical root cause judge for all assessed refined run tasks?')) return
    setRerunningRootCauseJudge(true)
    try {
      const res = await authedFetch('/api/rerun-root-cause-judge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        const result = await res.json()
        alert(`Root cause judge rerun: ${result.updated} updated, ${result.skipped} skipped out of ${result.total} tasks`)
        fetchData()
        dispatch(fetchFailures({ tag: null, search: null }))
      }
    } catch {
      // ignore
    } finally {
      setRerunningRootCauseJudge(false)
    }
  }, [fetchData, dispatch])

  const [resettingRuns, setResettingRuns] = useState(false)
  const handleResetAllRuns = useCallback(async () => {
    if (!confirm('Reset ALL stage 4 runs? This deletes all failure runs and run tasks.')) return
    setResettingRuns(true)
    try {
      const res = await authedFetch('/api/reset-failures', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: 'run', resetAll: true }),
      })
      if (res.ok) {
        fetchData()
        dispatch(fetchFailures({ tag: null, search: null }))
      }
    } catch {
      // ignore
    } finally {
      setResettingRuns(false)
    }
  }, [fetchData, dispatch])

  const [recomputingTags, setRecomputingTags] = useState(false)
  const handleRecomputeTags = useCallback(async () => {
    setRecomputingTags(true)
    try {
      const res = await authedFetch('/api/recompute-tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        const result = await res.json() as { processed: number; errors: number; total: number }
        alert(`Recomputed tags: ${result.processed}/${result.total} updated, ${result.errors} errors`)
        fetchData()
        dispatch(fetchFailures({ tag: null, search: null }))
      }
    } catch {
      // ignore
    } finally {
      setRecomputingTags(false)
    }
  }, [fetchData, dispatch])

  const [resettingSummarization, setResettingSummarization] = useState(false)
  const handleResetSummarizationWorker = useCallback(async () => {
    setResettingSummarization(true)
    try {
      const res = await authedFetch('/api/reset-summarization-worker', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        const result = await res.json() as { reset_count: number }
        alert(`Summarization worker reset. ${result.reset_count} stuck failure(s) cleared.`)
        fetchData()
      }
    } catch {
      // ignore
    } finally {
      setResettingSummarization(false)
    }
  }, [fetchData])

  const cleanRunningCount = data ? data.cleanEvents.filter((e) => e.status === 'running').length : 0
  const refineRunningCount = data ? data.refineEvents.filter((e) => e.status === 'running').length : 0
  const rootCauseRunningCount = data ? data.rootCauseEvents.filter((e) => e.status === 'running').length : 0

  const uncleanedPending = data ? data.uncleaned.filter((f) => f.clean_status !== 'failed').length : 0
  const uncleanedFailed = data ? data.uncleaned.filter((f) => f.clean_status === 'failed').length : 0
  const unrefinedPending = data ? data.unrefined.filter((f) => f.refined_status !== 'failed').length : 0
  const unrefinedFailed = data ? data.unrefined.filter((f) => f.refined_status === 'failed').length : 0
  const unrootcausedPending = data ? (data.unrootcaused || []).filter((f) => f.root_cause_status !== 'failed').length : 0
  const unrootcausedFailed = data ? (data.unrootcaused || []).filter((f) => f.root_cause_status === 'failed').length : 0

  const pipelineRunning = data ? data.pipelineFailures.filter(f => f.run_status === 'running').length : 0

  const runPending = data ? data.pendingRunFailures.filter(f => f.run_status !== 'failed').length : 0
  const runFailed = data ? data.pendingRunFailures.filter(f => f.run_status === 'failed').length : 0
  const summarizePending = data ? data.pendingSummarizeFailures.filter(f => f.summarize_status !== 'failed').length : 0
  const summarizeFailed = data ? data.pendingSummarizeFailures.filter(f => f.summarize_status === 'failed').length : 0

  return (
    <div data-testid="processing-page" className="processing-page">
      <SidebarNavigation />

      <main
        className="app-page-main"
        style={{
          ['--processing-section-sticky-top' as string]: `${sectionHeaderStickyTop > 0 ? sectionHeaderStickyTop : 180}px`,
        }}
      >
        <header ref={pageStickyHeaderRef} className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner">
            <div className="processing-header">
              <h1 className="app-page-title">Failure Processing</h1>
            </div>

            <div className="processing-toggles">
              <label data-testid="processing-auto-clean-toggle" className="processing-toggle">
                <input
                  type="checkbox"
                  checked={autoCleanEnabled}
                  onChange={handleToggleAutoClean}
                  className="processing-toggle-input"
                />
                <span className="processing-toggle-label">Auto-Clean</span>
              </label>
              <label data-testid="processing-auto-refine-toggle" className="processing-toggle">
                <input
                  type="checkbox"
                  checked={autoRefineEnabled}
                  onChange={handleToggleAutoRefine}
                  className="processing-toggle-input"
                />
                <span className="processing-toggle-label">Auto-Refine</span>
              </label>
              <label data-testid="processing-auto-root-cause-toggle" className="processing-toggle">
                <input
                  type="checkbox"
                  checked={autoRootCauseEnabled}
                  onChange={handleToggleAutoRootCause}
                  className="processing-toggle-input"
                />
                <span className="processing-toggle-label">Auto-RCA</span>
              </label>
              <label data-testid="processing-auto-run-toggle" className="processing-toggle">
                <input
                  type="checkbox"
                  checked={autoRunEnabled}
                  onChange={handleToggleAutoRun}
                  className="processing-toggle-input"
                />
                <span className="processing-toggle-label">Auto-Run</span>
              </label>
              <label data-testid="processing-auto-summarize-toggle" className="processing-toggle">
                <input
                  type="checkbox"
                  checked={autoSummarizeEnabled}
                  onChange={handleToggleAutoSummarize}
                  className="processing-toggle-input"
                />
                <span className="processing-toggle-label">Auto-Summarize</span>
              </label>
            </div>
          </div>
        </header>

        {loading && (
          <div data-testid="processing-loading" className="processing-empty">Loading processing data...</div>
        )}

        {!loading && data && (
          <>
            <ProcessingSection
              testId="cleaning-section"
              title="Clean"
              count={data.uncleaned.length}
              pendingCount={uncleanedPending}
              failedCount={uncleanedFailed}
              runningCount={cleanRunningCount}
              expanded={expandedSections.cleaning ?? false}
              onToggle={() => toggleSection('cleaning')}
              onResetFailures={() => handleResetFailures('clean')}
            >
              <h3 className="processing-subsection-title">Uncleaned Failures</h3>
              {data.uncleaned.length === 0 ? (
                <div className="processing-empty">All failures have been cleaned.</div>
              ) : (
                <ul className="processing-list">
                  {data.uncleaned.map((f) => (
                    <li key={f.id} data-testid="uncleaned-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-row-link" data-testid="uncleaned-item-link">
                        <span className="processing-list-short-id">{f.short_id}</span>
                        <span className="processing-list-title">{f.title}</span>
                        <span className={`processing-list-status ${f.clean_status === 'failed' ? 'failed' : ''}`}>
                          {f.clean_status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="processing-subsection-title">Cleaning Log</h3>
              {cleanRunningCount > 0 && (
                <div className="processing-log-summary">{cleanRunningCount} currently in progress</div>
              )}
              {data.cleanEvents.length === 0 ? (
                <div className="processing-empty">No cleaning attempts yet.</div>
              ) : (
                <ul className="processing-list">
                  {data.cleanEvents.map((e) => (
                    <li key={e.id} data-testid="clean-event-item" className="processing-list-item">
                      <Link to={`/failures/${e.failure_id}`} className="processing-list-short-id processing-list-link">{e.failure_short_id}</Link>
                      <Link to={`/failures/${e.failure_id}`} className="processing-list-title processing-list-link">{e.failure_title}</Link>
                      <span className={`processing-list-status ${e.status}`}>{e.status}</span>
                      <span className="processing-list-time">{formatTime(e.created_at)}{e.ended_at ? ` — ${formatTime(e.ended_at)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </ProcessingSection>

            <ProcessingSection
              testId="refining-section"
              title="Refine"
              count={data.unrefined.length}
              pendingCount={unrefinedPending}
              failedCount={unrefinedFailed}
              runningCount={refineRunningCount}
              expanded={expandedSections.refining ?? false}
              onToggle={() => toggleSection('refining')}
              onResetFailures={() => handleResetFailures('refine')}
            >
              <h3 className="processing-subsection-title">Unrefined Failures</h3>
              {data.unrefined.length === 0 ? (
                <div className="processing-empty">All cleaned failures have been refined.</div>
              ) : (
                <ul className="processing-list">
                  {data.unrefined.map((f) => (
                    <li key={f.id} data-testid="unrefined-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-row-link" data-testid="unrefined-item-link">
                        <span className="processing-list-short-id">{f.short_id}</span>
                        <span className="processing-list-title">{f.title}</span>
                        <span className={`processing-list-status ${f.refined_status === 'failed' ? 'failed' : ''}`}>
                          {f.refined_status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="processing-subsection-title">Refining Log</h3>
              {refineRunningCount > 0 && (
                <div className="processing-log-summary">{refineRunningCount} currently in progress</div>
              )}
              {data.refineEvents.length === 0 ? (
                <div className="processing-empty">No refining attempts yet.</div>
              ) : (
                <ul className="processing-list">
                  {data.refineEvents.map((e) => (
                    <li key={e.id} data-testid="refine-event-item" className="processing-list-item">
                      <Link to={`/failures/${e.failure_id}`} className="processing-list-short-id processing-list-link">{e.failure_short_id}</Link>
                      <Link to={`/failures/${e.failure_id}`} className="processing-list-title processing-list-link">{e.failure_title}</Link>
                      <span className={`processing-list-status ${e.status}`}>{e.status}</span>
                      <span className="processing-list-time">{formatTime(e.created_at)}{e.ended_at ? ` — ${formatTime(e.ended_at)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </ProcessingSection>

            <ProcessingSection
              testId="root-cause-section"
              title="Root Cause"
              count={(data.unrootcaused || []).length}
              pendingCount={unrootcausedPending}
              failedCount={unrootcausedFailed}
              runningCount={rootCauseRunningCount}
              expanded={expandedSections.rootCause ?? false}
              onToggle={() => toggleSection('rootCause')}
              onResetFailures={() => handleResetFailures('root_cause')}
            >
              <h3 className="processing-subsection-title">Pending Root Cause Analysis</h3>
              {(data.unrootcaused || []).length === 0 ? (
                <div className="processing-empty">All refined failures have root causes.</div>
              ) : (
                <ul className="processing-list">
                  {(data.unrootcaused || []).map((f) => (
                    <li key={f.id} data-testid="unrootcaused-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-row-link" data-testid="unrootcaused-item-link">
                        <span className="processing-list-short-id">{f.short_id}</span>
                        <span className="processing-list-title">{f.title}</span>
                        <span className={`processing-list-status ${f.root_cause_status === 'failed' ? 'failed' : ''}`}>
                          {f.root_cause_status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="processing-subsection-title">Root Cause Analysis Log</h3>
              {rootCauseRunningCount > 0 && (
                <div className="processing-log-summary">{rootCauseRunningCount} currently in progress</div>
              )}
              {(data.rootCauseEvents || []).length === 0 ? (
                <div className="processing-empty">No root cause analysis attempts yet.</div>
              ) : (
                <ul className="processing-list">
                  {(data.rootCauseEvents || []).map((e) => (
                    <li key={e.id} data-testid="root-cause-event-item" className="processing-list-item">
                      <Link to={`/failures/${e.failure_id}`} className="processing-list-short-id processing-list-link">{e.failure_short_id}</Link>
                      <Link to={`/failures/${e.failure_id}`} className="processing-list-title processing-list-link">{e.failure_title}</Link>
                      <span className={`processing-list-status ${e.status}`}>{e.status}</span>
                      <span className="processing-list-time">{formatTime(e.created_at)}{e.ended_at ? ` — ${formatTime(e.ended_at)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </ProcessingSection>

            <ProcessingSection
              testId="run-section"
              title="Run"
              count={data.pendingRunFailures.length}
              pendingCount={runPending}
              failedCount={runFailed}
              runningCount={pipelineRunning + data.runContainers.filter(c => c.status === 'running').length}
              expanded={expandedSections.run ?? false}
              onToggle={() => toggleSection('run')}
              onResetFailures={() => handleResetFailures('run')}
            >
              <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  data-testid="reset-all-runs-btn"
                  className="processing-section-reset-btn"
                  onClick={handleResetAllRuns}
                  disabled={resettingRuns}
                  style={{ fontSize: '0.8rem' }}
                >
                  {resettingRuns ? 'Resetting...' : 'Reset All Runs'}
                </button>
                <button
                  data-testid="sync-run-agents-btn"
                  className="processing-section-reset-btn"
                  onClick={handleSyncRunAgents}
                  disabled={syncingRunAgents}
                  style={{ fontSize: '0.8rem' }}
                >
                  {syncingRunAgents ? 'Syncing...' : 'Sync Run Agents'}
                </button>
                <button
                  data-testid="rerun-root-cause-judge-btn"
                  className="processing-section-reset-btn"
                  onClick={handleRerunRootCauseJudge}
                  disabled={rerunningRootCauseJudge}
                  style={{ fontSize: '0.8rem' }}
                >
                  {rerunningRootCauseJudge ? 'Rerunning...' : 'Rerun Root Cause Judge'}
                </button>
              </div>
              <h3 className="processing-subsection-title">Pending Runs</h3>
              {data.pendingRunFailures.length === 0 ? (
                <div className="processing-empty">All failures with root causes have been run.</div>
              ) : (
                <ul className="processing-list">
                  {data.pendingRunFailures.map((f) => (
                    <li key={f.id} data-testid="pending-run-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-row-link">
                        <span className="processing-list-short-id">{f.short_id}</span>
                        <span className="processing-list-title">{f.title}</span>
                        <span className={`processing-list-status ${f.run_status === 'failed' ? 'failed' : ''}`}>
                          {f.run_status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="processing-subsection-title">Run Log</h3>
              {data.pipelineFailures.filter(f => f.run_status).length === 0 && data.runContainers.length === 0 ? (
                <div className="processing-empty">No run activity yet.</div>
              ) : (
                <ul className="processing-list">
                  {data.runContainers.map((c) => (
                    <li key={c.id} data-testid="run-container-item" className="processing-list-item processing-list-item--run-row">
                      <span className="processing-list-short-id">{c.container_name}</span>
                      <span className="processing-list-title">
                        {c.is_failure_run && c.failure_short_id ? (
                          <Link to={`/failures/${c.source_failure_id}`} className="processing-list-link">
                            FRUN: {c.failure_short_id}
                          </Link>
                        ) : (
                          <Link to={`/runs/${c.run_id}`} className="processing-list-link">
                            {c.run_name}
                          </Link>
                        )}
                      </span>
                      <span className="processing-list-title">{c.agent_name}</span>
                      <span className={`processing-list-status ${c.status}`}>{c.status}</span>
                      <span className="processing-list-time">{formatTime(c.created_at)}</span>
                    </li>
                  ))}
                  {data.pipelineFailures.filter(f => f.run_status).map((f) => (
                    <li key={`run-${f.id}`} data-testid="pipeline-run-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-short-id processing-list-link">{f.short_id}</Link>
                      <Link to={`/failures/${f.id}`} className="processing-list-title processing-list-link">{f.title}</Link>
                      <span className={`processing-list-status ${f.run_status}`}>{f.run_status}</span>
                      {f.run_error && <span className="processing-list-error">{f.run_error}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </ProcessingSection>

            <ProcessingSection
              testId="summarize-section"
              title="Summarize"
              count={data.pendingSummarizeFailures.length}
              pendingCount={summarizePending}
              failedCount={summarizeFailed}
              runningCount={data.summarizingCount}
              expanded={expandedSections.summarize ?? false}
              onToggle={() => toggleSection('summarize')}
              onResetFailures={() => handleResetFailures('summarize')}
            >
              <div className="processing-actions">
                <button
                  data-testid="recompute-tags-btn"
                  className="processing-section-reset-btn"
                  disabled={recomputingTags}
                  onClick={handleRecomputeTags}
                >
                  {recomputingTags ? 'Recomputing...' : 'Recompute Tags'}
                </button>
                <button
                  data-testid="reset-summarization-worker-btn"
                  className="processing-section-reset-btn"
                  disabled={resettingSummarization}
                  onClick={handleResetSummarizationWorker}
                >
                  {resettingSummarization ? 'Resetting...' : 'Reset Summarization Worker'}
                </button>
              </div>

              <h3 className="processing-subsection-title">Pending Summarization</h3>
              {data.pendingSummarizeFailures.length === 0 ? (
                <div className="processing-empty">All run failures have been summarized.</div>
              ) : (
                <ul className="processing-list">
                  {data.pendingSummarizeFailures.map((f) => (
                    <li key={f.id} data-testid="pending-summarize-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-row-link">
                        <span className="processing-list-short-id">{f.short_id}</span>
                        <span className="processing-list-title">{f.title}</span>
                        <span className={`processing-list-status ${f.summarize_status === 'failed' ? 'failed' : ''}`}>
                          {f.summarize_status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="processing-subsection-title">Summarize Log</h3>
              {data.pipelineFailures.filter(f => f.summarize_status).length === 0 ? (
                <div className="processing-empty">No summarize activity yet.</div>
              ) : (
                <ul className="processing-list">
                  {data.pipelineFailures.filter(f => f.summarize_status).map((f) => (
                    <li key={`sum-${f.id}`} data-testid="pipeline-summarize-item" className="processing-list-item">
                      <Link to={`/failures/${f.id}`} className="processing-list-short-id processing-list-link">{f.short_id}</Link>
                      <Link to={`/failures/${f.id}`} className="processing-list-title processing-list-link">{f.title}</Link>
                      <span className={`processing-list-status ${f.summarize_status}`}>{f.summarize_status}</span>
                      {f.summarize_error && <span className="processing-list-error">{f.summarize_error}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </ProcessingSection>
          </>
        )}
      </main>
    </div>
  )
}

export default ProcessingPage
